// @flow
import * as React from 'react'
import deprecate from 'mz-utils/deprecate'
import tinycolor from 'tinycolor2'
import shallowEqual from 'mz-utils/shallowEqual'
import { withWrapper } from 'styletron-react'

// Types
export type PaletteColours = $Keys<typeof COLOURS>
export type AppearanceTypes = $Keys<typeof DEFAULT_APPEARANCES>
export type PaletteColorGetter = (string) => tinycolor
export type GetColorFn = (?string) => tinycolor
export type ProviderProps = {
  colors?: CustomColours,
  defaultAppearances?: AppearanceObject,
  children: React.Node,
}
export type CustomColours = {
  [key: PaletteColours]: string | tinycolor,
}
export type AppearanceObject = {
  [key: AppearanceTypes]: string,
}
type PaletteContextType = {
  getColor: GetColorFn,
  getAppearance: (AppearanceTypes, customAppearanceName?: string) => string,
}

// Internals
export const COLOURS = {
  PRIMARY: tinycolor('#428BCA'),
  SECONDARY: tinycolor('#428BCA'),
  TEXT: tinycolor('#212121'),
  GRAY: tinycolor('#DDDDDD'),
  GREEN: tinycolor('#58B76A'),
  BLUE: tinycolor('#428BCA'),
  RED: tinycolor('#C7254E'),
  WHITE: tinycolor('#FFFFFF'),
  BLACK: tinycolor('#000000'),
  YELLOW: tinycolor('#FFC107'),
  ORANGE: tinycolor('#FFA000'),
  TRANSPARENT: tinycolor({ r: 0, g: 0, b: 0, a: 0 }),
  CTA: tinycolor('#FFC107'),
}
export const DEFAULT_APPEARANCES = {
  BASE_FIELD: 'material',
  BUTTON: 'general',
}

const getColorFromObject = (
  palette: Object,
  rawColor: ?string | tinycolor
): tinycolor => {
  if (typeof rawColor !== 'string') return rawColor

  const color = (rawColor || '').toUpperCase()
  return palette[color] ? palette[color].clone() : tinycolor(color)
}

const getAppearanceFromObject = (
  defaultAppearances: AppearanceObject,
  componentName: AppearanceTypes,
  customAppearanceName?: string
): string => {
  if (customAppearanceName) return customAppearanceName
  const appearance = defaultAppearances[componentName]
  if (!appearance) {
    throw new Error(`There is no appearance for component ${componentName}`)
  }
  return appearance
}

const getDefaultColor = (rawColor: ?string): tinycolor =>
  getColorFromObject(COLOURS, rawColor)

const getDefaultAppearance = (
  componentName: AppearanceTypes,
  customAppearanceName?: string
): string =>
  getAppearanceFromObject(
    DEFAULT_APPEARANCES,
    componentName,
    customAppearanceName
  )

export const PaletteContext = React.createContext<PaletteContextType>({
  getColor: getDefaultColor,
  getAppearance: getDefaultAppearance,
})

/**
 * A provider component that adds `mzPalette` object to react's context
 * with two functions: `getColor` and `updateColours`. They will allow you
 * to get palette from any place of the react app and update palette from
 * any point as well.
 */
export class PaletteProvider extends React.Component<ProviderProps> {
  iteration = 0

  palette: $Shape<CustomColours> = COLOURS

  defaultAppearances: $Shape<AppearanceObject> = DEFAULT_APPEARANCES

  unmounted: boolean

  UNSAFE_componentWillMount() {
    this.updateColours(this.props.colors, true)
    this.updateDefaultAppearances(this.props.defaultAppearances, true)
  }

  UNSAFE_componentWillReceiveProps(nextProps: ProviderProps) {
    if (!shallowEqual(nextProps.colors, this.props.colors)) {
      this.updateColours(nextProps.colors, false)
    }
    if (
      !shallowEqual(nextProps.defaultAppearances, this.props.defaultAppearances)
    ) {
      this.updateDefaultAppearances(nextProps.defaultAppearances, false)
    }
  }

  componentWillUnmpunt() {
    this.unmounted = true
  }

  updateDefaultAppearances = (
    defaultAppearances: ?AppearanceObject,
    dontRerender: boolean
  ) => {
    if (!defaultAppearances) return

    const newAppearances = { ...DEFAULT_APPEARANCES }
    Object.keys(defaultAppearances).forEach((rawComponentName) => {
      const componentName = rawComponentName.toUpperCase()
      const appearanceName = defaultAppearances[rawComponentName].toLowerCase()
      // eslint-disable-next-line no-param-reassign
      newAppearances[componentName] = appearanceName
    })

    this.defaultAppearances = newAppearances
    this.iteration += 1

    if (!dontRerender && !this.unmounted) {
      this.forceUpdate()
    }
  }

  updateColours = (colours: ?CustomColours, dontRerender: boolean) => {
    if (!colours) return

    const newPalette = { ...COLOURS }
    Object.keys(colours).forEach((rawColorName) => {
      const colorName = rawColorName.toUpperCase()
      // eslint-disable-next-line no-param-reassign
      newPalette[colorName] = tinycolor(colours[rawColorName])
    })

    this.palette = newPalette
    this.iteration += 1

    if (!dontRerender && !this.unmounted) {
      this.forceUpdate()
    }
  }

  getColor = (rawColor: ?string) => getColorFromObject(this.palette, rawColor)

  getAppearance = (
    componentName: AppearanceTypes,
    customAppearanceName?: string
  ) =>
    getAppearanceFromObject(
      this.defaultAppearances,
      componentName,
      customAppearanceName
    )

  render() {
    const contextValue = {
      getColor: this.getColor,
      getAppearance: this.getAppearance,
    }

    return (
      <PaletteContext.Provider key={this.iteration} value={contextValue}>
        {this.props.children}
      </PaletteContext.Provider>
    )
  }
}

/**
 * Simple tiny component to provide palette color getter to render prop
 * @param  {Object} props
 * @return {Component}
 */
export const ProvidePalette = (props: {
  render: (PaletteColorGetter) => React.Node,
}) => {
  const { getColor } = React.useContext(PaletteContext)
  return props.render(getColor)
}

/**
 * A function tha adds to props for your component `$palette` prop, which
 * is a function to get a color from palette.
 * @param  {Component}
 * @return {Component}
 */
export const withPalette = deprecate((Component: *) => {
  return withWrapper(Component, (Styled) =>
    React.forwardRef((props, ref) => {
      const { getColor } = React.useContext(PaletteContext)
      return <Styled {...props} $palette={getColor} ref={ref} />
    })
  )
}, 'withPalette')

/**
 * Returns a tinycolor instance by name or HEX from active palette
 * @param  {string} color
 * @return {tinycolor}
 */
export const usePaletteColor = (color: string) => {
  const { getColor } = React.useContext(PaletteContext)
  return getColor(color)
}

/**
 * Returns an appearance name for a given component type.
 * If custom appearance name provided then it will be returned instead of
 * the default one
 * @param  {AppearanceTypes} componentName
 * @param  {?String} customAppearanceName
 * @return {String}
 */
export const useAppearance = (
  componentName: AppearanceTypes,
  customAppearanceName?: string
) => {
  const { getAppearance } = React.useContext(PaletteContext)
  return getAppearance(componentName, customAppearanceName)
}

export default withPalette
